import type { ReactNode } from 'react';
import { useMemo } from 'react';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { ThemeProvider } from '@jotpsych/ui';

interface PipStyleProviderProps {
  pipWindow: Window;
  children: ReactNode;
}

/**
 * Provides MUI theme + Emotion cache scoped to the Document PiP window.
 *
 * Emotion's default cache targets `document.head` in the **opener** tab.
 * When we portal MUI components into a Document PiP window, `sx` prop styles
 * and component CSS are injected into the wrong document, so the PiP window
 * appears unstyled. Creating a cache whose `container` points at the PiP
 * window's `<head>` forces Emotion to write CSS there instead.
 */
export function PipStyleProvider({ pipWindow, children }: PipStyleProviderProps) {
  const cache = useMemo(() => {
    const container = pipWindow.document.head;
    return createCache({ key: 'pip', container });
  }, [pipWindow]);

  return (
    <CacheProvider value={cache}>
      <ThemeProvider>{children}</ThemeProvider>
    </CacheProvider>
  );
}
